import { useRef, useState } from "react";
import { useHub } from "../../../../core/Global";
import { API_ROUTES } from "../../../../core/Constants";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import TagifyJobs from "../../../../components/tagify/TagifyJobs";
import TagifyTechs from "../../../../components/tagify/TagifyTechs";
import Button from "../../../../components/base/Button";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { toastError } from "../../../../components/base/Toast";
import Job from "../models/Job";
import JobInvoice from "../models/JobInvoice";

interface IProps {
    onClose: () => void;
    onCreated: (invoice: JobInvoice) => void;
}

const DRAWER_ID = "drawer-create-invoice";

export default function JobInvoiceCreateDrawer(props: IProps) {

    const hub = useHub();

    const drawerRef = useRef<DrawerRef>(null);
    const createdRef = useRef<JobInvoice>();

    const [type, typeSet] = useState<"invoice" | "estimate">("invoice");
    const [jobId, jobIdSet] = useState<string>();
    const [techId, techIdSet] = useState<string>();
    const [creating, creatingSet] = useState<boolean>(false);

    // Called once the drawer is fully closed (overlay/body cleaned up by KTDrawer).
    const onDrawerClosed = () => {
        if (createdRef.current) {
            const inv = createdRef.current;
            createdRef.current = undefined;
            props.onCreated(inv);
        } else {
            props.onClose();
        }
    };

    const onJobSelected = (id: string) => {
        jobIdSet(id);
        if (!id) return;
        // Preselect the job's technician when it has one.
        hub.Get(API_ROUTES.Jobs + id, res => {
            if (res.success) {
                const job: Job = res.data;
                if (job?.techId) techIdSet(job.techId);
            }
        });
    };

    const create = () => {
        if (!jobId) {
            window.formError("jobId", "Please select a job!");
            return;
        }
        if (!techId) {
            window.formError("techId", "Please select a technician!");
            return;
        }

        creatingSet(true);
        hub.Post(API_ROUTES.JobInvoices + (type === "invoice" ? "invoice" : "estimate"), {
            jobId,
            techId
        }, res => {
            creatingSet(false);
            if (res.success) {
                // Close the drawer gracefully first; onCreated fires after it's hidden.
                createdRef.current = res.data;
                drawerRef.current?.close();
            }
            else {
                toastError(res.message);
            }
        });
    };

    return (
        <Drawer ref={drawerRef} id={DRAWER_ID} title="Create New" onClose={onDrawerClosed}>
            <div className="pt-3">

                <div className="fw-bold fs-7 mb-2">Type</div>
                <FlexRow className="mb-6">
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start me-4 px-5 py-3 ${type === "invoice" ? "active" : ""}`}>
                        <span className="form-check form-check-custom form-check-solid form-check-sm align-items-start">
                            <input className="form-check-input" type="radio" checked={type === "invoice"}
                                onChange={e => { if (e.target.checked) typeSet("invoice"); }} />
                        </span>
                        <span className="ms-2 fs-7 fw-bold">
                            <Icon icon="file-invoice-dollar" className="me-2" />Invoice
                        </span>
                    </label>
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-5 py-3 ${type === "estimate" ? "active" : ""}`}>
                        <span className="form-check form-check-custom form-check-solid form-check-sm align-items-start">
                            <input className="form-check-input" type="radio" checked={type === "estimate"}
                                onChange={e => { if (e.target.checked) typeSet("estimate"); }} />
                        </span>
                        <span className="ms-2 fs-7 fw-bold">
                            <Icon icon="file-invoice" className="me-2" />Estimate
                        </span>
                    </label>
                </FlexRow>

                <TagifyJobs id="drp-create-invoice-job" name="jobId" block
                    label="Job" labelUp placeholder="Select a job"
                    onSelect={(id) => onJobSelected(id ?? "")} onRemove={() => jobIdSet(undefined)} />

                <div className="mt-4">
                    <TagifyTechs id="drp-create-invoice-tech" name="techId" block
                        label="Technician" labelUp placeholder="Select a technician"
                        selectedId={techId}
                        onSelect={(id) => techIdSet(id)} onRemove={() => techIdSet(undefined)} />
                </div>

                <div className="mt-8">
                    <Button text={creating ? "Creating..." : ("Create " + (type === "invoice" ? "Invoice" : "Estimate"))}
                        icon="plus" color="success" large isRunning={creating} onClick={create} />
                </div>

            </div>
        </Drawer>
    );
}
